import type { ReactNode } from 'react';
import Image from 'next/image';
import classNames from 'styles/utils/classNames';
import type { MapVenue } from './types';

interface Props {
  venue: MapVenue;
  /** Rounded miles from the caller's origin, or null when there's nothing to
   *  measure from — the label then falls back to indoor/outdoor alone. */
  distance: number | null;
  action?: ReactNode;
  placeholderImageSrc: string;
}

/** The popup anchored to the active marker on desktop. */
export default function VenueMapCard({ venue, distance, action, placeholderImageSrc }: Props) {
  const hasIndoorLabel = venue.isIndoor != null;

  return (
    <div
      className={classNames(
        'flex w-40 shrink-0 cursor-pointer select-none flex-col overflow-hidden rounded-md text-color-text-lightmode-primary shadow-color-bg-lightmode-invert dark:text-color-text-darkmode-primary dark:shadow-color-bg-darkmode-invert',
        // Only reserve the taller card when there's an action to sit at the bottom;
        // without one the meta line would float away from the title.
        action && 'h-full min-h-40',
      )}
    >
      <div className="relative h-20 overflow-hidden rounded-tl-md rounded-tr-md bg-gray-50">
        <Image src={venue.imageUrl || placeholderImageSrc} alt="" fill />
      </div>
      <div className="flex flex-1 flex-col gap-1 rounded-bl-md rounded-br-md bg-color-bg-lightmode-primary p-2 dark:bg-color-bg-darkmode-primary">
        <span className="typography-product-button-label-xs">{venue.title || venue.address}</span>
        {(hasIndoorLabel || distance != null) && (
          <div
            className={classNames('typography-product-text-card flex gap-1', action && 'mt-auto')}
          >
            {hasIndoorLabel && <span>{venue.isIndoor ? 'Indoor' : 'Outdoor'}</span>}
            {hasIndoorLabel && distance != null && (
              <span className="text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
                &bull;
              </span>
            )}
            {distance != null && (
              <span>
                {distance} mile{distance !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
        {action}
      </div>
    </div>
  );
}
